<template>
  <div class="json-display">
    <!-- 标题区域 -->
    <div class="json-header">
      <span class="json-title">{{ title }}</span>
      <div class="json-actions">
        <!-- 复制按钮 -->
        <el-button
          v-if="showCopy"
          type="primary"
          size="small"
          :icon="copyIcon"
          @click="handleCopy"
          :loading="copyLoading"
        >
          {{ copyText }}
        </el-button>

        <!-- 格式化按钮 -->
        <el-button
          v-if="showFormat && !isFormatted"
          type="info"
          size="small"
          @click="handleFormat"
        >
          格式化
        </el-button>
      </div>
    </div>

    <!-- JSON 内容展示区域 -->
    <div class="json-content" :class="{ 'has-border': bordered }">
      <!-- 使用 textarea 显示内容 -->
      <el-input
        ref="textarea"
        type="textarea"
        :autosize="!fixedHeight ? { minRows: rows, maxRows: maxRows } : false"
        :rows="fixedHeight ? rows : null"
        :readonly="readonly"
        :placeholder="placeholder"
        v-model="displayContent"
        @input="handleInput"
        spellcheck="false"
        class="json-textarea"
        :class="{ 'readonly-textarea': readonly }"
      />

      <!-- 字符统计 -->
      <div v-if="showCharCount" class="char-count">
        字符数: {{ charCount }}
      </div>

      <!-- JSON 格式验证提示 -->
      <div v-if="showValidate && displayContent && !isValidJson" class="validate-error">
        <i class="el-icon-warning"></i> 不是有效的 JSON 格式
      </div>
    </div>
  </div>
</template>

<script>
import { Message } from 'element-ui'

export default {
  name: 'JsonDisplay',

  props: {
    // 支持 v-model
    value: {
      type: [Object, Array, String, Number, Boolean],
      default: null
    },

    // 标题
    title: {
      type: String,
      default: 'JSON 数据'
    },

    // 是否显示复制按钮
    showCopy: {
      type: Boolean,
      default: true
    },

    // 是否显示格式化按钮
    showFormat: {
      type: Boolean,
      default: true
    },

    // 是否显示 JSON 格式验证
    showValidate: {
      type: Boolean,
      default: false
    },

    // 是否只读
    readonly: {
      type: Boolean,
      default: true
    },

    // 是否自动格式化 JSON
    autoFormat: {
      type: Boolean,
      default: true
    },

    // 行数
    rows: {
      type: Number,
      default: 6
    },

    // 最大行数（仅在 autosize 模式下有效）
    maxRows: {
      type: Number,
      default: 20
    },

    // 是否固定高度
    fixedHeight: {
      type: Boolean,
      default: false
    },

    // 占位符
    placeholder: {
      type: String,
      default: '请输入 JSON 数据...'
    },

    // 是否有边框
    bordered: {
      type: Boolean,
      default: true
    },

    // 是否显示字符统计
    showCharCount: {
      type: Boolean,
      default: false
    },

    // 复制按钮文案
    copyText: {
      type: String,
      default: '复制'
    },

    // 复制成功后的文案
    copySuccessText: {
      type: String,
      default: '已复制'
    },

    // 复制成功后的恢复时间（毫秒）
    copyResetTime: {
      type: Number,
      default: 2000
    }
  },

  data() {
    return {
      displayContent: '',
      copyLoading: false,
      copySuccess: false,
      copyIcon: 'el-icon-document-copy',
      isFormatted: false
    }
  },

  computed: {
    // 计算字符数
    charCount() {
      return this.displayContent ? this.displayContent.length : 0
    },

    // 验证 JSON 格式
    isValidJson() {
      if (!this.displayContent || this.displayContent.trim() === '') {
        return true
      }

      try {
        JSON.parse(this.displayContent)
        return true
      } catch (e) {
        return false
      }
    }
  },

  watch: {
    value: {
      immediate: true,
      deep: true,
      handler(newVal) {
        this.updateDisplayContent(newVal)
      }
    }
  },

  mounted() {
    this.updateDisplayContent(this.value)
  },

  methods: {
    // 更新显示内容
    updateDisplayContent(value) {
      if (value === null || value === undefined) {
        this.displayContent = ''
        this.isFormatted = false
        return
      }

      let jsonString

      if (typeof value === 'object') {
        // 如果是对象或数组
        try {
          jsonString = this.autoFormat
            ? JSON.stringify(value, null, 2)
            : JSON.stringify(value)
          this.isFormatted = this.autoFormat
        } catch (e) {
          jsonString = String(value)
          this.isFormatted = false
        }
      } else if (typeof value === 'string') {
        // 如果是字符串
        jsonString = value

        // 检查是否已经是格式化的 JSON
        if (value.trim()) {
          try {
            JSON.parse(value)
            // 简单判断是否格式化（检查是否有缩进或换行）
            this.isFormatted = value.includes('\n') || value.includes('  ')
          } catch (e) {
            this.isFormatted = false
          }
        } else {
          this.isFormatted = false
        }
      } else {
        // 其他类型（数字、布尔值等）
        jsonString = String(value)
        this.isFormatted = false
      }

      this.displayContent = jsonString
    },

    // 处理复制
    handleCopy() {
      if (!this.displayContent) {
        Message.warning('没有内容可复制')
        return
      }

      this.copyLoading = true
      this.copyIcon = 'el-icon-loading'

      // 创建临时 textarea
      const textarea = document.createElement('textarea')
      textarea.value = this.displayContent
      textarea.style.position = 'fixed'
      textarea.style.opacity = 0
      document.body.appendChild(textarea)
      textarea.select()

      try {
        const successful = document.execCommand('copy')
        if (successful) {
          this.copySuccess = true
          this.copyIcon = 'el-icon-check'
          Message.success('复制成功')

          // 恢复按钮状态
          setTimeout(() => {
            this.copyLoading = false
            this.copyIcon = 'el-icon-document-copy'
            this.copySuccess = false
            this.$emit('copy-success')
          }, this.copyResetTime)
        } else {
          throw new Error('复制失败')
        }
      } catch (err) {
        console.error('复制失败:', err)
        Message.error('复制失败，请使用 Ctrl+C 手动复制')
        this.copyLoading = false
        this.copyIcon = 'el-icon-document-copy'
        this.$emit('copy-error', err)
      } finally {
        document.body.removeChild(textarea)
      }
    },

    // 处理格式化
    handleFormat() {
      if (!this.displayContent || this.displayContent.trim() === '') {
        Message.warning('请输入 JSON 内容')
        return
      }

      try {
        const jsonObj = JSON.parse(this.displayContent)
        this.displayContent = JSON.stringify(jsonObj, null, 2)
        this.isFormatted = true

        // 触发输入事件以更新父组件
        this.handleInput()

        Message.success('格式化成功')
        this.$emit('format-success', this.displayContent)
      } catch (e) {
        Message.error('JSON 格式错误，无法格式化')
        this.$emit('format-error', e)
      }
    },

    // 处理输入
    handleInput() {
      const value = this.displayContent

      // 更新父组件
      this.$emit('input', value)
      this.$emit('change', value)

      // 检查格式
      this.$emit('validate', this.isValidJson)

      // 检查是否格式化了
      if (value && value.trim()) {
        try {
          JSON.parse(value)
          this.isFormatted = value.includes('\n')
        } catch (e) {
          this.isFormatted = false
        }
      }
    },

    // 手动设置内容
    setContent(content, format = true) {
      if (format && content && typeof content === 'object') {
        try {
          this.displayContent = JSON.stringify(content, null, 2)
          this.isFormatted = true
        } catch (e) {
          this.displayContent = String(content)
          this.isFormatted = false
        }
      } else {
        this.displayContent = content
      }

      this.handleInput()
    },

    // 获取 JSON 对象
    getJson() {
      if (!this.displayContent || this.displayContent.trim() === '') {
        return null
      }

      try {
        return JSON.parse(this.displayContent)
      } catch (e) {
        throw new Error('内容不是合法的 JSON 格式')
      }
    },

    // 获取原始文本
    getText() {
      return this.displayContent
    },

    // 清空内容
    clear() {
      this.displayContent = ''
      this.handleInput()
    },

    // 检查格式是否有效
    checkValid() {
      return this.isValidJson
    },

    // 格式化内容（外部调用）
    format() {
      this.handleFormat()
    },

    // 复制内容（外部调用）
    copy() {
      this.handleCopy()
    }
  }
}
</script>

<style scoped>
.json-display {
  width: 98%;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', 'Courier New', monospace;
}

.json-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  flex-wrap: wrap;
}

.json-title {
  font-weight: 600;
  font-size: 14px;
  color: #606266;
  line-height: 32px;
}

.json-actions {
  display: flex;
  gap: 8px;
}

.json-actions .el-button {
  margin: 0;
}

.json-content {
  position: relative;
  width: 100%;
}

.json-content.has-border {
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  padding: 12px;
  background-color: #fafafa;
}

/* Element UI textarea 样式覆盖 */
.json-textarea >>> .el-textarea__inner {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', 'Courier New', monospace;
  font-size: 13px;
  line-height: 1.5;
  color: #333;
  background-color: transparent;
  border: none;
  padding: 0;
  resize: vertical;
  min-height: 60px;
}

.json-textarea.readonly-textarea >>> .el-textarea__inner {
  background-color: transparent;
  cursor: default;
}

.json-textarea >>> .el-textarea__inner:focus {
  border-color: transparent;
  box-shadow: none;
  outline: none;
}

.char-count {
  margin-top: 8px;
  text-align: right;
  font-size: 12px;
  color: #909399;
  line-height: 1;
}

.validate-error {
  margin-top: 8px;
  font-size: 12px;
  color: #f56c6c;
  display: flex;
  align-items: center;
  gap: 4px;
}

.validate-error i {
  font-size: 14px;
}

/* JSON 高亮样式（如果需要语法高亮，可以启用下面的样式） */
.json-highlight {
  white-space: pre-wrap;
  word-wrap: break-word;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', 'Courier New', monospace;
  font-size: 13px;
  line-height: 1.5;
}

.json-key {
  color: #c18401;
}

.json-string {
  color: #067d17;
}

.json-number {
  color: #1750eb;
}

.json-boolean {
  color: #c21f1f;
}

.json-null {
  color: #666;
}
</style>
